import {ModelApi} from '@/api/model.api.js'
import {bearerToken, csrfToken, reAuthenticationHandler} from '@/services/auth-session.js'
import {appVersion} from '@/constants/app.js'
import {useEnv} from '@/composables/use-env.js'
import {useRailsUrls} from '@/composables/use-rails-urls.js'
import {useTeams} from '@/composables/use-teams.js'

/**
 * Rails API base class
 *
 * Use this as a base class for interactions with the Rails API. It is safe to
 * use for both model-based and non-model-based endpoints.
 */
export class RailsApi extends ModelApi {

  //Convert params by default for the Rails API
  convertParamKeys = true
  convertDataKeys = true
  convertJsonKeys = true

  //Define base URL for child API service classes
  get baseUrl() {
    const {railsApiBaseUrl} = useRailsUrls()
    return railsApiBaseUrl.value
  }

  /**
   * Append default config
   */
  appendDefaultConfig(config) {

    //Parent method first (ensures headers object is present)
    super.appendDefaultConfig(config)

    //Extract config
    const {skipBearerToken, skipCsrfToken} = config

    //Get git hash and Teams app version
    const {gitHash} = useEnv()
    const {isInTeams, teamsAppId, teamsAppVersion} = useTeams()

    //Append to headers
    config.headers['X-App-Version'] = appVersion
    config.headers['X-App-Git-Hash'] = gitHash.value

    //Only append Teams app version if in Teams
    if (isInTeams.value) {
      config.headers['X-Teams-App-Id'] = teamsAppId.value
      config.headers['X-Teams-App-Version'] = teamsAppVersion.value
    }

    //Include CSRF token if present
    if (!skipCsrfToken && csrfToken.value) {
      config.headers['X-CSRF-Token'] = csrfToken.value
    }

    //Include bearer token if present
    if (!skipBearerToken && bearerToken.value) {
      config.headers['Authorization'] = `Bearer ${bearerToken.value}`
    }

    //Include cookie credentials
    config.credentials = 'include'
  }

  /**
   * Handle error
   */
  handleError(error) {

    //Handle 401 (invoke handler then still process + rethrow via parent — do not return
    //from handleError or the request catch will treat the failure as a successful resolve)
    if (error?.isUnauthenticatedError && reAuthenticationHandler.value) {
      const handler = reAuthenticationHandler.value
      handler()
    }

    //Use parent method
    super.handleError(error)
  }
}
